"use client"

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, Snackbar, Alert, Typography, Button } from '@mui/material';
import { Description } from '@mui/icons-material';
import SchemaDesigner from '../SchemaDesigner/SchemaDesigner';
import ClassDiagramDraw from '../ClassDiagram/ClassDiagramDraw';

export default function CombinedDiagramDraw({ 
  problemId, 
  schemaDesign, 
  setSchemaDesign,
  documentVisible = false, 
  setDocumentVisible = () => {},
  onUnsavedChangesChange = () => {}
}) {
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [localSchemaDesign, setLocalSchemaDesign] = useState(schemaDesign);
  
  // Track unsaved changes from both diagrams
  const [entityUnsavedChanges, setEntityUnsavedChanges] = useState(false);
  const [classUnsavedChanges, setClassUnsavedChanges] = useState(false);
  
  // Notify parent when either diagram has unsaved changes
  useEffect(() => {
    const hasAnyUnsavedChanges = entityUnsavedChanges || classUnsavedChanges;
    onUnsavedChangesChange(hasAnyUnsavedChanges);
  }, [entityUnsavedChanges, classUnsavedChanges, onUnsavedChangesChange]);
  
  // Refs to store deselectAll and clearCopiedItems functions from both diagrams
  const entityDeselectAllRef = useRef(null);
  const classDeselectAllRef = useRef(null);
  const entityClearCopiedRef = useRef(null);
  const classClearCopiedRef = useRef(null);
  
  // Resizable divider state
  const [dividerPosition, setDividerPosition] = useState(() => {
    const saved = localStorage.getItem(`lld-diagram-divider-${problemId}`);
    return saved ? parseFloat(saved) : 50; // Default 50% (equal split)
  });
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef(null);



  // Update local schema when prop changes
  useEffect(() => {
    if (schemaDesign) {
      setLocalSchemaDesign(schemaDesign);
    }
  }, [schemaDesign]);

  // Save divider position to localStorage
  useEffect(() => {
    if (problemId) {
      localStorage.setItem(`lld-diagram-divider-${problemId}`, dividerPosition.toString());
    }
  }, [dividerPosition, problemId]);

  // Handle selection change - deselect items in the other diagram
  const handleEntitySelectionChange = useCallback(() => {
    // When entity is selected, deselect all classes
    if (classDeselectAllRef.current) {
      classDeselectAllRef.current();
    }
  }, []);

  const handleClassSelectionChange = useCallback(() => {
    // When class is selected, deselect all entities
    if (entityDeselectAllRef.current) {
      entityDeselectAllRef.current();
    }
  }, []);

  // Handle copy - clear copied items in the other diagram
  const handleEntityCopy = useCallback(() => {
    // When entity is copied, clear copied items in class diagram
    if (classClearCopiedRef.current) {
      classClearCopiedRef.current();
    }
  }, []);

  const handleClassCopy = useCallback(() => {
    // When class is copied, clear copied items in entity diagram
    if (entityClearCopiedRef.current) {
      entityClearCopiedRef.current();
    }
  }, []);

  // Callbacks to receive deselectAll functions from diagrams
  const handleEntityDeselectAllCallback = useCallback((deselectAllFn) => {
    entityDeselectAllRef.current = deselectAllFn;
  }, []);

  const handleClassDeselectAllCallback = useCallback((deselectAllFn) => {
    classDeselectAllRef.current = deselectAllFn;
  }, []);

  // Callbacks to receive clearCopiedItems functions from diagrams
  const handleEntityClearCopiedCallback = useCallback((clearCopiedFn) => {
    entityClearCopiedRef.current = clearCopiedFn;
  }, []);

  const handleClassClearCopiedCallback = useCallback((clearCopiedFn) => {
    classClearCopiedRef.current = clearCopiedFn;
  }, []);

  // Handle divider drag
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || !containerRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = (x / rect.width) * 100;
    
    // Constrain between 20% and 80% to prevent sections from becoming too small
    const constrainedPercentage = Math.max(20, Math.min(80, percentage));
    setDividerPosition(constrainedPercentage);
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Compact Header */}
      <Box sx={{ 
        borderBottom: 1, 
        borderColor: 'divider',
        backgroundColor: '#f8f9fa',
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        px: 1.5,
        py: 0.5,
        minHeight: '40px',
        gap: 1
      }}>
        <Button
          variant="outlined"
          onClick={() => setDocumentVisible(!documentVisible)}
          startIcon={<Description />}
          size="small"
          sx={{
            borderColor: '#008080',
            color: '#008080',
            '&:hover': {
              backgroundColor: '#e3f2fd',
              borderColor: '#006666'
            },
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '12px',
            padding: '6px 12px',
            minHeight: '28px'
          }}
        >
          {documentVisible ? 'Hide' : 'View Doc'}
        </Button>
      </Box>

      {/* Side by Side Content with Resizable Divider */}
      <Box 
        ref={containerRef}
        sx={{ 
          flex: 1, 
          display: 'flex', 
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        {/* Entities Section - Left */}
        <Box sx={{ 
          width: `${dividerPosition}%`,
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: '200px',
          '& .MuiTypography-h6': {
            fontSize: '14px !important',
            fontWeight: 600
          },
          '& > div > div': {
            padding: '8px !important',
            '& button': {
              fontSize: '12px !important',
              padding: '4px 8px !important',
              height: '28px !important'
            }
          }
        }}>
          <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            <SchemaDesigner 
              problemId={problemId}
              schemaDesign={localSchemaDesign}
              setSchemaDesign={(val) => {
                setLocalSchemaDesign(val);
                if (setSchemaDesign) setSchemaDesign(val);
              }}
              componentType="LLD"
              documentVisible={documentVisible}
              setDocumentVisible={setDocumentVisible}
              hideEvaluate={true}
              hideViewDoc={true}
              onSelectionChange={handleEntitySelectionChange}
              deselectAllCallback={handleEntityDeselectAllCallback}
              onCopy={handleEntityCopy}
              clearCopiedCallback={handleEntityClearCopiedCallback}
              onUnsavedChangesChange={setEntityUnsavedChanges}
            />
          </Box>
        </Box>

        {/* Resizable Divider */}
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            width: '4px',
            backgroundColor: isDragging ? '#1976d2' : '#e0e0e0',
            cursor: 'col-resize',
            flexShrink: 0,
            position: 'relative',
            '&:hover': {
              backgroundColor: '#1976d2'
            },
            transition: isDragging ? 'none' : 'background-color 0.2s',
            zIndex: 10,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: '-2px',
              right: '-2px',
              top: 0,
              bottom: 0,
              cursor: 'col-resize'
            }
          }}
        />

        {/* Classes Section - Right */}
        <Box sx={{ 
          flex: 1,
          display: 'flex', 
          flexDirection: 'column',
          overflow: 'hidden',
          minWidth: '200px',
          '& .MuiTypography-h6': {
            fontSize: '14px !important',
            fontWeight: 600
          },
          '& > div > div': {
            padding: '8px !important',
            '& button': {
              fontSize: '12px !important',
              padding: '4px 8px !important',
              height: '28px !important'
            }
          }
        }}>
          <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
            <ClassDiagramDraw 
              problemId={problemId}
              documentVisible={documentVisible}
              setDocumentVisible={setDocumentVisible}
              hideEvaluate={true}
              hideViewDoc={true}
              onSelectionChange={handleClassSelectionChange}
              deselectAllCallback={handleClassDeselectAllCallback}
              onCopy={handleClassCopy}
              onUnsavedChangesChange={setClassUnsavedChanges}
              clearCopiedCallback={handleClassClearCopiedCallback}
            />
          </Box>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

